import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const TagInput = ({ onSelect, onClose }) => {
    const { authFetch } = useAuth();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
        
        // Clic fuera para cerrar
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        const fetchTags = async () => {
            setLoading(true);
            try {
                const searchParam = query ? `?query=${encodeURIComponent(query)}` : '?all=true';
                const response = await authFetch(`/api/tags/${searchParam}`);
                if (response.ok) {
                    const data = await response.json();
                    setSuggestions(data);
                }
            } catch (error) {
                console.error("Error fetching tags:", error);
            } finally {
                setLoading(false);
            }
        };

        const timerInfo = setTimeout(() => {
            fetchTags();
        }, 300);

        return () => clearTimeout(timerInfo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim() !== '') {
            onSelect(query.trim());
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <div ref={containerRef} className="absolute z-50 top-full mt-1 right-0 w-48 bg-[#1f2937] border border-gray-700 rounded-md shadow-lg overflow-hidden flex flex-col">
            <div className="flex items-center p-2 border-b border-gray-700">
                <Search size={14} className="text-gray-400 mr-2 flex-shrink-0" />
                <input 
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search or create tag..."
                    className="bg-transparent border-none outline-none text-xs text-white w-full"
                />
            </div>
            
            <div className="max-h-40 overflow-y-auto">
                {loading ? (
                    <div className="p-2 flex justify-center"><Loader2 size={16} className="text-gray-400 animate-spin" /></div>
                ) : suggestions.length > 0 ? (
                    <ul className="py-1">
                        {suggestions.map((tag) => (
                            <li 
                                key={tag.id}
                                className="px-3 py-1.5 text-xs text-gray-200 hover:bg-[#374151] cursor-pointer flex items-center"
                                onMouseDown={(e) => {
                                    e.preventDefault(); // Evitar blur
                                    onSelect(tag.name);
                                }}
                            >
                                <span 
                                    className="w-2 h-2 rounded-full mr-2" 
                                    style={{ backgroundColor: tag.color }}
                                ></span>
                                {tag.name}
                            </li>
                        ))}
                        {query && !suggestions.find(s => s.name.toLowerCase() === query.toLowerCase()) && (
                            <li 
                                className="px-3 py-1.5 text-xs text-blue-400 hover:bg-[#374151] cursor-pointer italic"
                                onMouseDown={(e) => { e.preventDefault(); onSelect(query); }}
                            >
                                Crear "{query}"
                            </li>
                        )}
                    </ul>
                ) : (
                    <div className="p-3 text-center text-xs text-gray-400">
                        {query ? (
                            <div 
                                className="text-blue-400 cursor-pointer hover:underline"
                                onMouseDown={(e) => { e.preventDefault(); onSelect(query); }}
                            >
                                Crear "{query}"
                            </div>
                        ) : 'Escribe para buscar...'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TagInput;
